import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { flatten } from "lodash";
import { Button, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AgGridReact } from "ag-grid-react";
import { fetchMatchedTransactions } from "../../../../redux/reducers/MatchedTransactions";
import { IDatasource } from "ag-grid-community";
import { TableFilterConfig } from "src/docuclipper/DocuclipperTypes";
import { fetchTls } from "src/redux/reducers/TransactionManager";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { datefilterParamsYYYYMMDD } from "../AnalyzeTransactionsTable";
import { DateRangeFloatingFilter } from "src/views/Docuclipper/AgGrid/DateRangeFloatingFilter";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import { useHasQbo } from "src/utils/utils";
import type { SankeyData } from "./FlowOfFundsChart";
import type { FlowOfFundsUrlFilters } from "./index";
import { useHistory, useLocation } from "react-router-dom";

const externalFilterColumns = [
  { field: "date", label: "Date", filter: true, filterType: "date", defaultFilterType: "equals" },
  { field: "description", label: "Description", filter: true, filterType: "text", defaultFilterType: "contains" },
  { field: "amount", label: "Amount", filter: true, filterType: "number", defaultFilterType: "equals" },
];

const columnDefs = [
  { field: "account" },
  { field: "date", filter: "agDateColumnFilter", filterParams: datefilterParamsYYYYMMDD, floatingFilterComponent: DateRangeFloatingFilter, floatingFilterComponentParams: { suppressFilterButton: true }, sortable: true, minWidth: 200 },
  { field: "description", filter: "agTextColumnFilter", filterParams: {} },
  { field: "amount", filter: "agNumberColumnFilter", sortable: true, filterParams: {} },
  { field: "category", filter: "agSetColumnFilter" },
];

const norm = (c: string | null | undefined) =>
  (c == null || c === "" ? "Uncategorized" : c);

/**
 * Convert URL filters into the table-side filter set understood by fetchTls.
 *
 *   - source/dest:        chart link/node selection. When source is "Income"
 *                         or dest matches an "Income:" category we narrow to
 *                         positive amounts; "Expenses" / "Expense:" -> negative.
 *                         When the link connects an account to a category we
 *                         additionally narrow on account + category.
 *   - selectedAccountName: name-based TL.account equality (chart account-node
 *                          click whose name didn't match an Account row).
 *   - accountIds / attributeFilters: forwarded as extra options so the server
 *                                    can resolve them via flow-of-funds-filters.
 */
function deriveTableFilters(
  apiFilters: FlowOfFundsUrlFilters,
  data2: SankeyData,
): {
  filters: TableFilterConfig[];
  accountIds?: Array<string | number>;
  attributeFilters?: Record<string, string | string[]>;
} {
  const filters: TableFilterConfig[] = [];

  if (apiFilters.selectedAccountName) {
    filters.push({
      field: "account",
      operator: "equals",
      value: apiFilters.selectedAccountName,
    });
  }

  const source = apiFilters.source;
  const dest = apiFilters.dest;
  if (source && dest) {
    const srcNode = data2.nodes.find((n) => n.name === source);
    const destNode = data2.nodes.find((n) => n.name === dest);

    // Income-side: source is an income category OR the literal "Income" node.
    const incomeSide =
      source === "Income" ||
      (typeof source === "string" && source.startsWith("Income:")) ||
      srcNode?.category === "income";

    // Expense-side: dest is the literal "Expenses" node OR an expense category.
    const expenseSide =
      dest === "Expenses" ||
      (typeof dest === "string" && dest.startsWith("Expense:")) ||
      destNode?.category === "expense";

    if (incomeSide) {
      filters.push({ field: "amount", operator: "greaterThan", value: 0 });
      // Category narrowing from "Income:<cat>" on either side.
      const cat =
        (source.split(":")[1] || dest.split(":")[1] || "").trim();
      if (cat) {
        filters.push({ field: "category", operator: "equals", value: cat });
      }
      // Account narrowing if dest is the account node.
      if (destNode?.category === "account") {
        filters.push({ field: "account", operator: "equals", value: dest });
      }
    } else if (expenseSide) {
      filters.push({ field: "amount", operator: "lessThan", value: 0 });
      const cat =
        (dest.split(":")[1] || source.split(":")[1] || "").trim();
      if (cat) {
        filters.push({ field: "category", operator: "equals", value: cat });
      }
      if (srcNode?.category === "account") {
        filters.push({ field: "account", operator: "equals", value: source });
      }
    } else if (srcNode?.category === "account" && destNode?.category === "account") {
      // Transfer between two accounts.
      filters.push({ field: "tx1Account", operator: "equals", value: source });
      filters.push({ field: "tx2Account", operator: "equals", value: dest });
    }
  }

  return {
    filters,
    accountIds: apiFilters.accountIds,
    attributeFilters: apiFilters.attributes,
  };
}

/** Table component that subscribes to TransactionManager - isolated from chart re-renders */
export function FlowOfFundsTable({
  apiFilters,
  data2,
  gridApiRef,
}: {
  apiFilters: FlowOfFundsUrlFilters;
  data2: SankeyData;
  gridApiRef: React.MutableRefObject<any>;
}) {
  const dispatch = useDispatch();
  const ref = useRef<any>(null);
  const history = useHistory();
  const location = useLocation();
  const {
    selectedDocumentId,
    tls,
    selectedChunk,
    accountChunkLabels,
    pagination,
    refreshKey,
  } = useSelector((state: ReduxState) => state.TransactionManager);

  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { matches } = useSelector(
    (state: ReduxState) => state.MatchedTransactions
  );
  const hasQbo = useHasQbo();
  const { documents } = tls;

  const [isGridReady, setIsGridReady] = useState(false);

  const latestForFetch = useRef({
    accountChunkLabels,
    documents,
    hasQbo,
    job,
  });
  latestForFetch.current = {
    accountChunkLabels,
    documents,
    hasQbo,
    job,
  };

  const matchedIds = useMemo(
    () =>
      flatten(
        (matches || [])
          .filter(
            (x) =>
              x?.match?.status === "approved" && Array.isArray(x?.transactions)
          )
          .map((x) => x.transactions)
      )
        .filter((x) => x && x.id)
        .map((x) => x.id),
    [matches]
  );

  useEffect(() => {
    if (matches.length === 0) {
      dispatch(fetchMatchedTransactions());
    }
  }, [matches.length, dispatch]);

  const tableFilterKey = useMemo(
    () =>
      JSON.stringify({
        a: apiFilters.attributes || null,
        i: apiFilters.accountIds || null,
        s: apiFilters.source || null,
        d: apiFilters.dest || null,
        sa: apiFilters.selectedAccountName || null,
      }),
    [
      apiFilters.attributes,
      apiFilters.accountIds,
      apiFilters.source,
      apiFilters.dest,
      apiFilters.selectedAccountName,
    ],
  );

  useEffect(() => {
    if (gridApiRef?.current && isGridReady) {
      gridApiRef.current.onFilterChanged();
    }
  }, [tableFilterKey, isGridReady, gridApiRef]);

  useEffect(() => {
    if (!gridApiRef?.current || !isGridReady) return;

    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: (params) => {
        const convertFilterModel = (): TableFilterConfig[] => {
          return Object.entries(params.filterModel || {}).map(
            ([field, filterModel]: [string, any]) => {
              const filterType = filterModel.filterType || "text";
              const operationType = filterModel.type;
              const filterOutput: any = {
                field,
                operator: operationType,
                valueFrom: undefined,
                valueTo: undefined,
                value: undefined,
              };

              if (filterType === "number") {
                if (operationType === "inRange") {
                  filterOutput.valueFrom = filterModel.filter;
                  filterOutput.valueTo = filterModel.filterTo;
                } else if (
                  operationType === "blank" ||
                  operationType === "notBlank"
                ) {
                  filterOutput.value = undefined;
                } else {
                  filterOutput.value = filterModel.filter;
                }
              } else if (filterType === "date") {
                if (operationType === "inRange") {
                  filterOutput.valueFrom = filterModel.dateFrom;
                  filterOutput.valueTo = filterModel.dateTo;
                } else if (
                  operationType === "blank" ||
                  operationType === "notBlank"
                ) {
                  filterOutput.value = undefined;
                } else {
                  filterOutput.value = filterModel.dateFrom;
                }
              } else {
                filterOutput.value = filterModel.filter;
              }
              return filterOutput;
            }
          );
        };

        const convertSortModel = (sortModel: any[]) =>
          sortModel.map((sort) => ({
            field: sort.colId,
            direction: sort.sort.toUpperCase(),
          }));

        const derived = deriveTableFilters(apiFilters, data2);

        dispatch(
          fetchTls(false, {
            pagination: {
              page: Math.floor(params.startRow / pagination.pageSize) + 1,
              pageSize: params.endRow - params.startRow,
            },
            filters: [
              { field: "included", operator: "equals" as const, value: true },
              ...convertFilterModel(),
              ...derived.filters,
            ],
            sorting: convertSortModel(params.sortModel || []),
            successCallback: params.successCallback,
            accountChunkLabels: latestForFetch.current.accountChunkLabels,
            documents: latestForFetch.current.documents,
            hasQbo: latestForFetch.current.hasQbo,
            job: latestForFetch.current.job,
            l1,
            l2,
            // FoF-specific filter passthrough (resolved on the server via
            // resolveAccountIds + MetadataFields tuple narrowing).
            ...(derived.accountIds && derived.accountIds.length > 0
              ? { accountIds: derived.accountIds }
              : {}),
            ...(derived.attributeFilters &&
            Object.keys(derived.attributeFilters).length > 0
              ? { attributeFilters: derived.attributeFilters }
              : {}),
          } as any)
        );
      },
    };

    gridApiRef.current.setDatasource(dataSource);
  }, [
    dispatch,
    pagination.pageSize,
    selectedDocumentId,
    selectedChunk,
    refreshKey,
    isGridReady,
    tableFilterKey,
    l1,
    l2,
    gridApiRef,
    apiFilters,
    data2,
  ]);

  // External-pass filter is still used for the chart-driven cosmetic narrowing.
  // Server-side filtering is now load-bearing (via deriveTableFilters); the
  // client filter just removes already-filtered-out rows if they slip through.
  const filterFn = useCallback(
    (rowNode: any) => {
      const source = apiFilters.source;
      const dest = apiFilters.dest;
      if (!source || !dest) return true;

      const amount = parseFloat(rowNode.data?.amount);
      const rowCategory = norm(rowNode.data?.category);

      if (
        source === "Income" &&
        typeof dest === "string" &&
        dest.startsWith("Income:")
      ) {
        const categoryName =
          (dest.split(":")[1] || "").trim() || "Uncategorized";
        return (
          !matchedIds.includes(rowNode.data?.id) &&
          rowCategory === categoryName &&
          amount > 0
        );
      }

      if (
        dest === "Expenses" &&
        typeof source === "string" &&
        source.startsWith("Expense:")
      ) {
        const categoryName =
          (source.split(":")[1] || "").trim() || "Uncategorized";
        return (
          !matchedIds.includes(rowNode.data?.id) &&
          rowCategory === categoryName &&
          amount < 0
        );
      }

      if (data2.nodes.find((n) => n.name === source)?.category === "income") {
        const categoryFromSource =
          (String(source).split(":")[1] || "").trim() || "Uncategorized";
        return (
          !matchedIds.includes(rowNode.data?.id) &&
          rowNode.data?.account === dest &&
          amount > 0 &&
          rowCategory === categoryFromSource
        );
      }

      if (
        data2.nodes.find((n) => n.name === dest)?.category === "expense"
      ) {
        const categoryFromDest =
          (String(dest).split(":")[1] || "").trim() || "Uncategorized";
        return (
          !matchedIds.includes(rowNode.data?.id) &&
          rowNode.data?.account === source &&
          amount < 0 &&
          rowCategory === categoryFromDest
        );
      }

      if (matchedIds.includes(rowNode.data?.id)) {
        const match = matches.find((x) =>
          x.transactions.some((t) => t.id === rowNode.data?.id)
        );
        if (match) {
          const [tx1, tx2] = match.transactions;
          return (
            (tx1.account === source && tx2.account === dest) ||
            (tx1.account === dest && tx2.account === source)
          );
        }
      }

      return false;
    },
    [apiFilters.source, apiFilters.dest, matchedIds, matches, data2.nodes]
  );

  const defaultColDef = useMemo(() => ({ resizable: true }), []);

  const onGridReady = useCallback(
    (params: any) => {
      gridApiRef.current = params.api;
      setIsGridReady(true);
      params.api.sizeColumnsToFit();
    },
    [gridApiRef]
  );

  const clearAllUrlFilters = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const toDrop: string[] = [];
    params.forEach((_, key) => {
      if (
        key.startsWith("filter[") ||
        key === "accountIds" ||
        key === "selectedAccountName" ||
        key === "source" ||
        key === "dest"
      ) {
        toDrop.push(key);
      }
    });
    toDrop.forEach((k) => params.delete(k));
    const out = params.toString();
    history.replace({
      pathname: location.pathname,
      search: out ? `?${out}` : "",
    });
  }, [history, location.pathname, location.search]);

  const hasActiveFilter =
    !!apiFilters.selectedAccountName ||
    !!apiFilters.source ||
    !!apiFilters.dest ||
    (apiFilters.accountIds || []).length > 0 ||
    (apiFilters.attributes && Object.keys(apiFilters.attributes).length > 0);

  const sourceNodeCategory = apiFilters.source
    ? data2.nodes.find((n) => n.name === apiFilters.source)?.category
    : undefined;
  const destNodeCategory = apiFilters.dest
    ? data2.nodes.find((n) => n.name === apiFilters.dest)?.category
    : undefined;

  return (
    <>
      <div className="h3 mt-4">Transactions</div>
      <Row>
        <Col>
          Filter by
          <ExternalFilter
            columnDefs={externalFilterColumns}
            gridApi={gridApiRef.current}
          />
          <Row className="align-items-center">
            <div className="filter-info ml-3">
              {hasActiveFilter && <span>Filter: </span>}
              {apiFilters.selectedAccountName && (
                <span>Account: {apiFilters.selectedAccountName}</span>
              )}
              {apiFilters.source && apiFilters.dest && (
                <span className="ml-1">
                  {apiFilters.source} → {apiFilters.dest}
                </span>
              )}
              {sourceNodeCategory === "income" && (
                <span className="ml-1 text-success font-weight-bold">
                  Income only (amount &gt; 0)
                </span>
              )}
              {destNodeCategory === "expense" && (
                <span className="ml-1 text-danger font-weight-bold">
                  Expense only (amount &lt; 0)
                </span>
              )}
            </div>
            {hasActiveFilter && (
              <Button variant="link" onClick={clearAllUrlFilters}>
                <FontAwesomeIcon icon="trash" />
                <span className="ml-1">Clear Filters</span>
              </Button>
            )}
          </Row>
        </Col>
      </Row>

      <Row>
        <div className="ag-theme-alpine" style={{ height: 600, width: "100%" }}>
          <AgGridReact
            ref={ref}
            singleClickEdit
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            isExternalFilterPresent={() => true}
            doesExternalFilterPass={filterFn}
            pagination={true}
            paginationPageSize={pagination.pageSize}
            rowModelType={"infinite"}
            cacheBlockSize={pagination.pageSize}
            cacheOverflowSize={2}
            maxConcurrentDatasourceRequests={1}
            infiniteInitialRowCount={1}
            maxBlocksInCache={2}
            rowData={undefined}
            onGridReady={onGridReady}
          />
        </div>
      </Row>
    </>
  );
}
